import React, { useState, useEffect, useContext, useRef } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';

import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';
import moment from 'moment';
import ReactToPrint from 'react-to-print';

import { DataContext } from '../../../context/DataContext';
import { simpleMessage } from '../../../helpers/Helpers';
import {
	getGestoresAsync,
	getGestoresBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../api/Users';
import { PrintReport } from '../../../components/PrintReport';
import NoData from '../../../components/NoData';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { getRol, getToken, getUser } from '../../../api/Account';
import { getVariacionCapitalAsync } from '../../../api/Report';

const VariacionCapital = () => {
	const [data, setData] = useState([]);

	const [sucursalName, setSucursalName] = useState('');
	const [sucursalList, setSucursalList] = useState([]);

	const [userList, setUserList] = useState([]);

	const [gestorName, setGestorName] = useState('');
	const [gestorList, setGestorList] = useState([]);

	const [selectedGestores, setSelectedGestores] = useState([]);

	const compRef = useRef();
	const { selectedSucursal, selectedUser, fechaDesde, fechaHasta, moneda, tipoCredito } =
		useParams();

	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	useEffect(() => {
		(async () => {
			let sucursals = await getSucursals();

			if (selectedSucursal === 't') {
				setSucursalName('Todas');
			} else {
				let name = sucursals.filter((item) => item.id.toString() === selectedSucursal);
				setSucursalName(name[0].description);
			}

			const cadenaDeNumeros = selectedUser;
			const arrayDeStrings = cadenaDeNumeros.split(',');
			const listaDeNumeros = arrayDeStrings.map((str) => Number(str));

			setSelectedGestores(listaDeNumeros);

			const data = {
				desde: fechaDesde.toString(),
				hasta: fechaHasta.toString(),
				gestorId: listaDeNumeros,
				sucursalId: selectedSucursal === 't' ? 0 : selectedSucursal,
				moneda,
				tipoCredito: tipoCredito === 't' ? 0 : tipoCredito
			};

			setIsLoading(true);
			const result = await getVariacionCapitalAsync(token, data);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			setIsLoading(false);

			setData(result.data.result);
		})();
	}, []);

	const getSucursals = async () => {
		setIsLoading(true);
		const result = await getSucursalByUserAsync(token);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		setSucursalList(
			selectedSucursal === 't'
				? result.data.result
				: result.data.result.filter((g) => g.id.toString() === selectedSucursal)
		);

		let gestores = await getUsers(selectedSucursal === 't' ? 0 : selectedSucursal);

		const cadenaDeNumeros = selectedUser;
		const arrayDeStrings = cadenaDeNumeros.split(',');
		const listaDeNumeros = arrayDeStrings.map((str) => Number(str));

		if (listaDeNumeros.length === 1) {
			let name = gestores.filter(
				(item) => item.id.toString() === listaDeNumeros[0].toString()
			);
			setGestorName(name[0].fullName);
		} else {
			let usuarios = '';
			listaDeNumeros.map((id) => {
				let name = gestores.filter((item) => item.id.toString() === id.toString());
				usuarios += name[0].fullName + ', ';
			});

			usuarios = usuarios.substring(0, usuarios.length - 2);
			setGestorName(usuarios);
		}
		return result.data.result;
	};

	const getUsers = async (sucursal) => {
		setIsLoading(true);
		const result =
			sucursal === 't'
				? await getGestoresBySucursalAccessAsync(token)
				: await getGestoresAsync(token, sucursal);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setUserList(result.data.result);
		return result.data.result;
	};

	const formatDate = (d) => moment(d).format('L');

	const formatMoney = (v) =>
		new Intl.NumberFormat(moneda === '1' ? 'es-NI' : 'en-US', {
			style: 'currency',
			currency: moneda === '1' ? 'NIO' : 'USD'
		}).format(v);

	const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }  
  @page {  margin: 3mm; size: landscape;  }
`;

	const exportExcel = () => {
		let exp = [];
		data.map((item) => {
			const dataGestor = userList.filter((gestor) => gestor.id === item.gestor);
			let gestor = dataGestor[0];

			exp.push({
				'#': item.id,
				Cliente: item.fullName,
				'Monto Entregado': formatMoney(item.montoEntregado),
				'Tasa Interes': item.intCorriente + ' %',
				Periodicidad: item.periodicidad,
				Plazo: item.plazo,
				Tipo: item.tipo,
				'S. Capital Inicial': formatMoney(item.saldoCapitalInicial),
				'Capital Abonado': formatMoney(item.abonoCapital),
				'Interes Abonado': formatMoney(item.abonoInteres),
				'Total Abonado': formatMoney(item.totalAbonado),
				'Cuotas Abonadas': item.cuotasAbonadas,
				'S. Capital Final': formatMoney(item.saldoCapitalFinal),
				sucursal: item.sucursal,
				Gestor: gestor ? gestor.fullName : '',
				moneda: item.isDolar ? 'Dolares' : 'Cordobas'
			});
		});

		var bk = XLSX.utils.book_new(),
			bs = XLSX.utils.json_to_sheet(exp);

		XLSX.utils.book_append_sheet(bk, bs, 'variacionCapital');
		XLSX.writeFile(bk, 'VariacionCapital.xlsx');
	};

	const paramText = (
		<Stack direction={'row'} justifyContent="space-between">
			<Stack>
				<Typography
					sx={{
						color: '#2196f3',
						textAlign: 'left'
					}}
					fontSize={11}
				>
					Parametros:
				</Typography>
				<Typography
					fontSize={11}
					sx={{
						textAlign: 'left'
					}}
				>
					{`Sucursal: ${
						selectedSucursal === 't' ? 'Todas' : sucursalList[0]?.description || ''
					}`}
				</Typography>

				<Typography fontSize={11} textAlign="left">
					{`Desde: ${moment(fechaDesde).format('L')}`}
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{`Hasta:  ${moment(fechaHasta).format('L')}`}
				</Typography>
			</Stack>

			<Stack>
				<Typography
					sx={{
						color: '#2196f3',
						textAlign: 'left'
					}}
					fontSize={11}
				>
					Usuario:
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{user}
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{rol}
				</Typography>
				<Typography fontSize={11} textAlign="left">
					{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
				</Typography>
			</Stack>
		</Stack>
	);

	let totalSaldoCapInicial = data.reduce((acc, r) => acc + Number(r.saldoCapitalInicial || 0), 0);
	let totalCapitalAbonado = data.reduce((acc, r) => acc + Number(r.abonoCapital || 0), 0);
	let totalInteresAbonado = data.reduce((acc, r) => acc + Number(r.abonoInteres || 0), 0);
	let totalSaldoCapitalFinal = data.reduce((acc, r) => acc + Number(r.saldoCapitalFinal || 0), 0);

	const DataTable = ({ data, headerFontSize, dataFontSize, isForPrint = false }) => {
		const saldoCapitalInicial = data.reduce(
			(acc, r) => acc + Number(r.saldoCapitalInicial || 0),
			0
		);
		const capitalAbonado = data.reduce((acc, r) => acc + Number(r.abonoCapital || 0), 0);
		const interesAbonado = data.reduce((acc, r) => acc + Number(r.abonoInteres || 0), 0);
		const saldoCapitalFinal = data.reduce(
			(acc, r) => acc + Number(r.saldoCapitalFinal || 0),
			0
		);

		// totalSaldoCapInicial += saldoCapitalInicial;
		// totalCapitalAbonado += capitalAbonado;
		// totalInteresAbonado += interesAbonado;

		const columns = [
			{ key: 'id', label: '#', align: 'center', getValue: (r) => r.id },
			{
				key: 'client',
				label: 'Cliente',
				align: 'left',
				getValue: (r) => r.fullName
			},
			{
				key: 'montoEntregado',
				label: 'Monto Entregado',
				align: 'center',
				getValue: (r) => formatMoney(r.montoEntregado)
			},
			{
				key: 'tasaInteres',
				label: 'Tasa Interes',
				align: 'center',
				getValue: (r) => r.intCorriente + ' %'
			},
			{
				key: 'periodicidad',
				label: 'Periodicidad',
				align: 'center',
				getValue: (r) => r.periodicidad
			},
			{
				key: 'plazo',
				label: 'Plazo',
				align: 'center',
				getValue: (r) => r.plazo
			},
			{
				key: 'tipo',
				label: 'Tipo',
				align: 'center',
				getValue: (r) => r.tipo
			},
			{
				key: 'saldoCapitalInicial',
				label: 'S.Capital Inicial',
				align: 'center',
				getValue: (r) => formatMoney(r.saldoCapitalInicial)
			},
			{
				key: 'saldoCapitalAbonado',
				label: 'Capital Abonado',
				align: 'center',
				getValue: (r) => formatMoney(r.abonoCapital)
			},
			{
				key: 'saldoInteresAbonado',
				label: 'Interes Abonado',
				align: 'center',
				getValue: (r) => formatMoney(r.abonoInteres)
			},
			{
				key: 'totalAbonado',
				label: 'Total Abonado',
				align: 'center',
				getValue: (r) => formatMoney(r.totalAbonado)
			},
			{
				key: 'cuotasAbonadas',
				label: 'Cuotas Abonadas',
				align: 'center',
				getValue: (r) => r.cuotasAbonadas
			},
			{
				key: 'saldoCapitalFinal',
				label: 'S. Capital Final',
				align: 'center',
				getValue: (r) => formatMoney(r.saldoCapitalFinal)
			}
		];

		return (
			<>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: headerFontSize }}
										className="text-primary fw-bold"
									>
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{data.map((r) => (
								<TableRow key={r.id}>
									{columns.map((col) => (
										<TableCell
											key={col.key}
											align={col.align}
											style={{ fontSize: dataFontSize }}
											className={isForPrint ? 'text-dark' : ''}
										>
											{col.getValue(r)}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				<hr />
				<Stack direction="row" flex="row" justifyContent="space-around" alignItems="center">
					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Créditos del Gestor
						</Typography>
						<Typography style={{ fontSize: 11 }}>{data.length}</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Capital Inicial
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{formatMoney(saldoCapitalInicial)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Capital Abonado
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{formatMoney(capitalAbonado)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Interes Abonado
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{formatMoney(interesAbonado)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Saldo Capital Final
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{formatMoney(saldoCapitalFinal)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontSize: 11,
								color:
									saldoCapitalFinal - saldoCapitalInicial >= 0
										? '#4caf50'
										: '#f44336'
							}}
						>
							{saldoCapitalFinal - saldoCapitalInicial >= 0
								? 'AUMENTO 📈'
								: 'DISMINUCION 📉'}{' '}
							{formatMoney(saldoCapitalFinal - saldoCapitalInicial)}
						</Typography>
					</Stack>
				</Stack>
				<hr />
			</>
		);
	};

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton variant="outlined" onClick={() => exportExcel()}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 40, color: '#00a152', width: 40 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte Gastos
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => {
								return (
									<IconButton variant="outlined">
										<PrintRoundedIcon
											style={{ fontSize: 40, color: '#2979ff', width: 40 }}
										/>
									</IconButton>
								);
							}}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>
				</Stack>

				<Container maxWidth={false} sx={{ textAlign: 'center' }}>
					<hr />

					{isEmpty(data) ? (
						<NoData />
					) : (
						sucursalList.map((suc) => {
							const sSucursal = data.filter(
								(c) => c.sucursalNombre === suc.description
							);

							return (
								<Stack key={suc.description}>
									<Typography
										fontSize={11}
										color="#2196f3"
										textAlign="left"
										fontWeight="bold"
									>
										Sucursal: {suc.description}
									</Typography>

									{userList.map((gestor) => {
										const cGestor = sSucursal.filter(
											(c) => c.gestor === gestor.id
										);

										return isEmpty(cGestor) ? null : (
											<Stack key={gestor.id}>
												<Stack direction="row" spacing={1}>
													<Typography
														sx={{
															color: '#2196f3',
															textAlign: 'left'
														}}
														fontSize={10}
													>
														Gestor:
													</Typography>
													<Typography
														sx={{
															textAlign: 'left'
														}}
														fontSize={10}
													>
														{gestor.fullName}
													</Typography>
												</Stack>

												<DataTable
													data={cGestor}
													headerFontSize={11}
													dataFontSize={10}
												/>
											</Stack>
										);
									})}
								</Stack>
							);
						})
					)}

					{selectedGestores.length === 1 ? null : (
						<Stack>
							<Typography
								style={{
									fontWeight: 'bold',
									color: '#03a9f4'
								}}
							>
								Consolidado
							</Typography>
							<hr />

							<Stack
								direction="row"
								flex="row"
								justifyContent="space-around"
								alignItems="center"
							>
								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Total Créditos
									</Typography>
									<Typography style={{ fontSize: 11 }}>{data.length}</Typography>
								</Stack>

								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Capital Inicial
									</Typography>
									<Typography style={{ fontSize: 11 }}>
										{formatMoney(totalSaldoCapInicial)}
									</Typography>
								</Stack>

								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Capital Abonado
									</Typography>
									<Typography style={{ fontSize: 11 }}>
										{formatMoney(totalCapitalAbonado)}
									</Typography>
								</Stack>

								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Interes Abonado
									</Typography>
									<Typography style={{ fontSize: 11 }}>
										{formatMoney(totalInteresAbonado)}
									</Typography>
								</Stack>

								<Stack textAlign="center">
									<Typography
										style={{
											fontWeight: 'bold',
											color: '#03a9f4',
											fontSize: 11
										}}
									>
										Saldo Capital Final
									</Typography>
									<Typography style={{ fontSize: 11 }}>
										{formatMoney(totalSaldoCapitalFinal)}
									</Typography>
								</Stack>

								<Stack textAlign="center">
									<Typography
										style={{
											fontSize: 11,
											color:
												totalSaldoCapitalFinal - totalSaldoCapInicial >= 0
													? '#4caf50'
													: '#f44336'
										}}
									>
										{totalSaldoCapitalFinal - totalSaldoCapInicial >= 0
											? 'AUMENTO 📈'
											: 'DISMINUCION 📉'}{' '}
										{formatMoney(totalSaldoCapitalFinal - totalSaldoCapInicial)}
									</Typography>
								</Stack>
							</Stack>
							<hr />
						</Stack>
					)}
				</Container>
			</Dialog>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport
					ref={compRef}
					titulo={'Reporte Variación de Capital'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />

						{isEmpty(data) ? (
							<NoData />
						) : (
							sucursalList.map((suc) => {
								const sSucursal = data.filter(
									(c) => c.sucursalNombre === suc.description
								);

								return (
									<Stack key={suc.description}>
										<Typography
											fontSize={11}
											color="#2196f3"
											textAlign="left"
											fontWeight="bold"
										>
											Sucursal: {suc.description}
										</Typography>

										{userList.map((gestor) => {
											const cGestor = sSucursal.filter(
												(c) => c.gestor === gestor.id
											);

											return (
												<Stack key={gestor.id}>
													<Stack direction="row" spacing={1}>
														<Typography
															sx={{
																color: '#2196f3',
																textAlign: 'left'
															}}
															fontSize={10}
														>
															Gestor:
														</Typography>
														<Typography
															sx={{
																textAlign: 'left'
															}}
															fontSize={10}
														>
															{gestor.fullName}
														</Typography>
													</Stack>

													<DataTable
														data={cGestor}
														headerFontSize={10}
														dataFontSize={9}
														isForPrint
													/>
												</Stack>
											);
										})}
									</Stack>
								);
							})
						)}

						{selectedGestores.length === 1 ? null : (
							<Stack>
								<Typography
									style={{
										fontWeight: 'bold',
										color: '#03a9f4'
									}}
								>
									Consolidado
								</Typography>
								<hr />

								<Stack
									direction="row"
									flex="row"
									justifyContent="space-around"
									alignItems="center"
								>
									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Total Créditos
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{data.length}
										</Typography>
									</Stack>

									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Capital Inicial
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{formatMoney(totalSaldoCapInicial)}
										</Typography>
									</Stack>

									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Capital Abonado
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{formatMoney(totalCapitalAbonado)}
										</Typography>
									</Stack>

									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Interes Abonado
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{formatMoney(totalInteresAbonado)}
										</Typography>
									</Stack>

									<Stack textAlign="center">
										<Typography
											style={{
												fontWeight: 'bold',
												color: '#03a9f4',
												fontSize: 11
											}}
										>
											Saldo Capital Final
										</Typography>
										<Typography style={{ fontSize: 11 }}>
											{formatMoney(totalSaldoCapitalFinal)}
										</Typography>
									</Stack>

									<Stack textAlign="center">
										<Typography
											style={{
												fontSize: 11,
												color:
													totalSaldoCapitalFinal - totalSaldoCapInicial >=
													0
														? '#4caf50'
														: '#f44336'
											}}
										>
											{totalSaldoCapitalFinal - totalSaldoCapInicial >= 0
												? 'AUMENTO 📈'
												: 'DISMINUCION 📉'}{' '}
											{formatMoney(
												totalSaldoCapitalFinal - totalSaldoCapInicial
											)}
										</Typography>
									</Stack>
								</Stack>
								<hr />
							</Stack>
						)}
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default VariacionCapital;
